import { openMailto } from "@/utils/mailto";
import Hero from "../components/hero";
import heroImage from "@assets/images/gas-solution-hero.svg";
import { commercialBillingSolution, whyChoose } from "../data";
import arrowRightIcon from "@assets/icons/arrow-narrow-right-gray.svg";
import tickIcon from "@assets/icons/tick-circle.svg";
import dashboardImage from "@assets/images/commercial-solution-dashboard.png";
import RequestCommericalProposalForm from "../components/requestCommercialProposalForm";
import Help from "../components/help";

const heroData = {
  title:
    "Powering the UAE’s Restaurants, Hotels, and Cloud Kitchens with Safe & Reliable Gas",
  description:
    "GASIS delivers commercial grade gas supply, installation, maintenance, and emergency services tailored to the demanding needs of F&B and hospitality businesses.",
  image: heroImage,
  buttonText: "Book a Safety Inspection",
  buttonOnclick: () => openMailto({ subject: "Safety Inspection Request", body: "Hello,\n\nI would like to book a safety inspection.\n\nFacility Address:\nContact Person:\nPhone:\nPreferred Date:\n" }),
  footerText:
    "Our licensed technicians and 24/7 emergency response team are always ready to protect your home and business.",
  footerButtonText: "Report an Emergency Online",
  footerButtonOnclick: () => openMailto({ subject: "Emergency Report", body: "Emergency Type:\nLocation:\nDescription:\n" }),
};

function CommercialGasSolution() {
  return (
    <div className="bg-white">
      <Hero data={heroData} />

      <section className="py-10 md:py-14 lg:py-[110px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-gis-dark-blue text-center">
            Why F&B Businesses Choose GASIS
          </h2>
          <p className="mt-2 text-[#62636C] text-center text-[14px] md:text-sm lg:text-lg">
            Trusted by restaurants, hotels, and cloud kitchens across the UAE
          </p>

          <div className="grid gap-4 mt-5 md:mt-8 md:grid-cols-2 lg:mt-16 xl:grid-cols-3">
            {whyChoose.map((service, index) => (
              <article
                key={index}
                className="p-3 md:p-6 border border-[#62636C33] rounded-[12px] md:rounded-[24px]"
              >
                <div className="flex flex-row items-center gap-3">
                  <div className="bg-black size-10 md:size-[48px] lg:size-[56px] grid place-content-center rounded-md md:rounded-[12px] flex-shrink-0">
                    <span className="text-xs font-semibold text-white md:text-base">
                      0{index + 1}
                    </span>
                  </div>
                  <h3 className="text-[#090D2C] text-sm md:text-lg font-semibold leading-[1.1]">
                    {service.title}
                  </h3>
                </div>
                <p className="mt-4 text-[14px] md:text-xs md:mt-6 lg:text-sm">
                  {service.desc}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* COMMERCIAL KITCHEN */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-gis-dark-blue text-center">
            Services Tailored for Commercial Kitchens
          </h2>
          <p className="mt-2 text-[#62636C] text-center text-[14px] md:text-sm lg:text-lg">
            Comprehensive gas solutions for restaurants, hotels & commercial
            kitchens
          </p>

          <div className="flex flex-col justify-between gap-5 mt-10 md:gap-10 lg:flex-row">
            <div className="lg:max-w-[465px] divide-y divide-[#B9BBC6]">
              {[
                "Gas System Design & Consultancy",
                "Supply & Installation",
                "Safety Inspections & Audits",
                "Operation & Maintenance (O&M) Contracts",
                "Emergency Services (24/7)",
              ].map((data, index) => (
                <div
                  key={index}
                  className={`flex p-3 md:p-6 justify-between items-center gap-3 ${index === 0 ? "bg-white" : ""}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-black size-8 md:size-[48px] grid place-content-center rounded-md md:rounded-[12px] flex-shrink-0">
                      <span className="text-[14px] font-semibold text-white md:text-base">
                        0{index + 1}
                      </span>
                    </div>

                    <h4 className="text-xs md:text-sm lg:text-base text-[#090D2C]">
                      {data}
                    </h4>
                  </div>

                  <img src={arrowRightIcon} alt="arrow right icon" />
                </div>
              ))}
            </div>
            <div className="lg:max-w-[820px] bg-white text-[#62636C] p-5 md:p-10 flex flex-col md:flex-row gap-5 md:gap-10 rounded-[12px] md:rounded-[24px] border border-[#62636C33]">
              <div>
                <span className=" text-[14px] md:text-base">
                  We design high-efficiency gas systems for:
                </span>
                <div className="mt-2 space-y-2 md:space-y-3.5">
                  {[
                    "Professional kitchens",
                    "Hotel catering facilities",
                    "Cloud kitchens",
                    "Bakeries & roasting plants",
                    "Food production units",
                    "Outdoor grills & BBQ areas",
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <span className="flex-shrink-0 rounded-full size-2 md:size-3 bg-[#62636C]" />
                      <span className="text-[#62636C] text-[14px] md:text-base">
                        {feature}
                      </span>
                    </li>
                  ))}
                </div>
              </div>
              <div>
                <span className=" text-[14px] md:text-base">
                  Services include:
                </span>
                <div className="mt-2 space-y-2 md:space-y-3.5">
                  {[
                    "Load calculations",
                    "Pipeline design",
                    "Commercial regulator sizing",
                    "Ventilation & hood integration",
                    "Fire alarm interfacing",
                    "CAD drawings & DCD submissions",
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <span className="flex-shrink-0 rounded-full size-2 md:size-3 bg-[#62636C]" />
                      <span className="text-[#62636C] text-[14px] md:text-base">
                        {feature}
                      </span>
                    </li>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPLIANCE REQUIREMENTS */}
      <section className="py-10 md:py-14 lg:py-[110px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto flex flex-col lg:flex-row gap-10 justify-between">
          <div className="lg:max-w-[630px]">
            <h2 className="text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-gis-dark-blue">
              Compliance Requirements for Commercial Kitchens
            </h2>
            <p className="mt-2 text-[#62636C] text-[14px] md:text-sm lg:text-lg">
              UAE commercial kitchen regulations are strict — and for good
              reason. GASIS ensures you pass all inspections smoothly and
              maintain continuous compliance.
            </p>

            <div className="mt-6 md:mt-12 py-4 md:py-6 px-5 md:px-12 text-[14px] md:text-sm lg:text-lg text-[#62636C] bg-[#F1F1F1] rounded-md md:rounded-lg">
              <h4>Need a compliance check?</h4>
              <p className="mt-4 leading-tight">
                Our experts will assess your kitchen and provide a detailed
                report with recommendations.
              </p>
            </div>
          </div>

          <div className="lg:max-w-[645px] flex-shrink-0">
            <h4 className="text-[#62636C] text-xs md:text-base lg:text-xl">
              UAE Commercial Kitchen Requirements
            </h4>
            <div className="mt-2">
              {[
                "Gas solenoid valve connected to fire alarm",
                "Proper ventilation/hood/duct systems",
                "ESMA-certified hoses & regulators",
                "Accessible emergency shutoff valve",
                "Regular hood/duct cleaning",
                "Gas detector installation",
                "Mechanical room compliance",
                "Annual safety inspection",
                "Quarterly ventilation hygiene checks",
              ].map((data, index) => (
                <article
                  key={index}
                  className="flex items-center gap-3 px-4 py-2 md:py-3"
                >
                  <img
                    src={tickIcon}
                    alt="tick icon"
                    className="size-4 md:size-5"
                  />
                  <span className="text-[#62636C] text-[14px] md:text-sm lg:text-lg">
                    {data}
                  </span>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMMERCIAL BILLING SOLUTION */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-gis-dark-blue text-center">
            Commercial Billing Solutions
          </h2>
          <p className="mt-3 text-[#62636C] text-center text-[14px] md:text-sm lg:text-lg max-w-[740px] mx-auto">
            We provide full billing services that reduce administrative workload
            for restaurants and hotel operators.
          </p>

          <div className="grid mt-6 md:mt-12 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-4 lg:gap-y-10">
            {commercialBillingSolution.map((data, index) => (
              <article
                key={index}
                className="p-6 text-center bg-white rounded-md md:p-8 md:rounded-lg"
              >
                <h3 className="text-tomato-200 text-sm md:text-lg font-medium leading-[1.1]">
                  {data.title}
                </h3>
                <p className="mt-2 text-[14px] md:text-xs lg:text-base text-[#62636C]">
                  {data.desc}
                </p>
              </article>
            ))}
          </div>

          <img
            src={dashboardImage}
            alt="dashboard image"
            className="mt-6 md:mt-12"
          />

          <div className="pt-20 md:pt-32 lg:pt-[180px]">
            <h2 className="text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-gis-dark-blue text-center">
              Request a Commercial Proposal
            </h2>

            <RequestCommericalProposalForm />
          </div>
        </div>
      </section>

      {/* HELP */}
      <Help />
    </div>
  );
}

export default CommercialGasSolution;
